"use client";

import { type CSSProperties, useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { Property } from "@/lib/types/marketsuite/datatypes/property";
import PropertyCard from "@/components/frontend/marketsuite/property/card";
import { MsRoutes } from "@/lib/types/marketsuite/property-route";
import { Button, type ButtonVariant } from "@/components/frontend/ui/button";
import { FeaturedPropertiesBlock } from "@/payload-types";

interface PropertyOverviewCarouselProps {
	properties: Partial<Property>[];
	settings: FeaturedPropertiesBlock["settings"];
	className?: string;
	msRoutes: MsRoutes;
	imagePriorityLoad: boolean;
	/** Unique id linking the navigation buttons to this carousel instance. */
	navId: string;
}

const bottomNavigationMap = {
	"bottom-right": "justify-end",
	"bottom-center": "justify-center",
};

/**
 * Renders the previous/next buttons for a carousel instance.
 *
 * @param navId - The `navId` of the carousel the buttons control.
 */
export function PropertyOverviewCarouselNav({ navId, variant, className }: { navId: string; variant?: ButtonVariant; className?: string }) {
	return (
		<div className={cn("flex gap-2", className)}>
			<Button
				variant={variant}
				type="button"
				aria-label="Previous property"
				className={cn("[&.swiper-button-disabled]:cursor-not-allowed [&.swiper-button-disabled]:opacity-40", `slider-prev-${navId}`)}
			>
				<ArrowLeft className="size-4" />
			</Button>

			<Button
				variant={variant}
				type="button"
				aria-label="Next property"
				className={cn("[&.swiper-button-disabled]:cursor-not-allowed [&.swiper-button-disabled]:opacity-40", `slider-next-${navId}`)}
			>
				<ArrowRight className="size-4" />
			</Button>
		</div>
	);
}

export function PropertyOverviewCarousel({ properties, settings, className, msRoutes, imagePriorityLoad, navId }: PropertyOverviewCarouselProps) {
	const [isMounted, setIsMounted] = useState(false);
	const desktopSlidesPerView = Math.min(parseInt(settings?.columns ?? "3"), properties.length);
	const navpos = settings?.navpos ?? "bottom-center";

	useEffect(() => {
		setIsMounted(true);
	}, []);

	if (!properties || properties.length === 0) return null;

	if (!isMounted) {
		return (
			<div
				style={{ "--slides-per-view-desktop": desktopSlidesPerView } as CSSProperties}
				className={cn(
					"grid w-full grid-flow-col gap-6 overflow-hidden [--slides-per-view:1.1] auto-cols-[calc((100%-(var(--slides-per-view)-1)*1.5rem)/var(--slides-per-view))] sm:[--slides-per-view:1.3] md:[--slides-per-view:2.1]",
					"lg:[--slides-per-view:var(--slides-per-view-desktop)]",
					className,
				)}
			>
				{properties.map((property) => (
					<PropertyCard
						key={property.id}
						urlBase={msRoutes[property.listing_type!]?.detailUrlBase ?? msRoutes[property.listing_type!]?.urlBase!}
						property={property}
						variant="default"
						imagePriorityLoad={imagePriorityLoad}
					/>
				))}
			</div>
		);
	}

	return (
		<div className={cn("relative w-full", className)}>
			<Swiper
				modules={[Navigation, Pagination]}
				className="overflow-x-clip! overflow-y-visible!"
				navigation={{ prevEl: `.slider-prev-${navId}`, nextEl: `.slider-next-${navId}` }}
				pagination={{ clickable: true }}
				style={{ "--swiper-pagination-color": "var(--ms-color-primary)" } as CSSProperties}
				spaceBetween={10}
				slidesPerView={1.1}
				breakpoints={{
					640: { slidesPerView: 1.3, spaceBetween: 18 },
					768: { slidesPerView: 2.1, spaceBetween: 20 },
					1024: { slidesPerView: desktopSlidesPerView, spaceBetween: 24 },
				}}
			>
				{properties.map((property) => {
					return (
						<SwiperSlide key={property.id} className="h-auto">
							<PropertyCard
								urlBase={msRoutes[property.listing_type!]?.detailUrlBase ?? msRoutes[property.listing_type!]?.urlBase!}
								property={property}
								variant="default"
								imagePriorityLoad={imagePriorityLoad}
							/>
						</SwiperSlide>
					);
				})}
			</Swiper>

			{navpos !== "top-right" && (
				<PropertyOverviewCarouselNav navId={navId} variant={settings?.navbtn} className={cn("mt-6", bottomNavigationMap[navpos])} />
			)}
		</div>
	);
}
